import { EmailAuthProvider } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { SAML_PROVIDER_ID_PREFIX } from '../constants/enum';

/**
 * SAML SSO ユーザーの判定ヘルパー（#3214）。
 * GCIP の SAML プロバイダ ID は 'saml.' で始まる（Gen Drive が作成するものは saml.gd-<companyId>）。
 * ID トークンの signInProvider（そのセッションのログイン方法）と
 * providerData（アカウントに紐づくログイン手段）の両方の判定に使う。
 */
export const isSamlProviderId = (providerId: string | null | undefined): boolean =>
  (providerId ?? '').startsWith(SAML_PROVIDER_ID_PREFIX);

/**
 * アカウントに紐づく SAML プロバイダ ID を返す（無い場合は null）。
 * @param user - ログイン中のユーザー（auth.currentUser）
 */
export const findSamlProviderId = (user: User | null | undefined): string | null =>
  user?.providerData.find(info => isSamlProviderId(info.providerId))?.providerId ?? null;

/**
 * パスワード（メール / パスワード）でログインできるアカウントか。
 * @param user - ログイン中のユーザー（auth.currentUser）
 */
export const hasPasswordProvider = (user: User | null | undefined): boolean =>
  user?.providerData.some(info => info.providerId === EmailAuthProvider.PROVIDER_ID) ?? false;

/**
 * 再認証（MFA の設定・解除）に SAML を使うべきユーザーか判定し、使う場合はプロバイダ ID を返す（#3214）。
 *
 * SAML と パスワードの両方が紐づくユーザー（samlMode.optional のテナントで併用しているケース）は
 * パスワード再認証を優先する。理由は次の 2 点。
 * - パスワードを持つユーザーはパスワード再設定で自力復旧できるが、テナントが SAML を無効化した後は
 *   IdP 再認証が失敗し、SAML 固定にすると解除手段が無くなる
 * - ポップアップを開かない分、ブロック等の失敗要因が少ない
 * パスワードを持たない（JIT プロビジョニングで作られた）SAML 専用ユーザーだけが IdP 再認証になる。
 *
 * @param user - ログイン中のユーザー（auth.currentUser）
 * @returns SAML 再認証に使うプロバイダ ID。パスワード再認証を使う場合は null
 */
export const resolveSamlReauthProviderId = (user: User | null | undefined): string | null =>
  hasPasswordProvider(user) ? null : findSamlProviderId(user);
